/** Shared personal mention syntax. Pure and browser-safe; Chatter and Team-Chat use exactly this contract. */
export interface MentionPerson { id: string; displayName: string }
export interface MentionToken { id: string; start: number; end: number }

/**
 * Erwähnte Personen, deren „@Anzeigename“ noch im Text steht (Web-App: gewählt per @-Auswahl, danach
 * vielleicht wieder gelöscht). Längere Namen zuerst: ein „@Tobias Beispiel“ zählt nicht zugleich für „Tobias“.
 * Reihenfolge wie `candidates`, ohne Doppelte.
 */
export function mentionedIds(body: string, candidates: readonly MentionPerson[]): string[] {
  const found = new Set(mentionTokens(body, candidates).map((token) => token.id));
  return [...new Set(candidates.map((person) => person.id))].filter((id) => found.has(id));
}
/** Same canonical recognition, with positions into the existing text instead of a second name snapshot. */
export function mentionTokens(body: string, candidates: readonly MentionPerson[]): MentionToken[] {
  let rest = body;
  const found: MentionToken[] = [];
  for (const person of [...candidates].sort((a, b) => b.displayName.length - a.displayName.length)) {
    const next = consumeMention(rest, person.displayName);
    if (next) {
      found.push(...next.tokens.map((token) => ({ ...token, id: person.id })));
      rest = next.body;
    }
  }
  return found.sort((a, b) => a.start - b.start);
}
/** The original Chatter boundary and longest-name semantics, shared by both callers. */
function consumeMention(body: string, displayName: string): { body: string; tokens: { start: number; end: number }[] } | null {
  const needle = `@${displayName}`;
  let out = body;
  const tokens: { start: number; end: number }[] = [];
  for (let i = out.indexOf(needle); i >= 0; i = out.indexOf(needle, i + 1)) {
    const next = out.charAt(i + needle.length);
    if (next === '' || !/[\p{L}\p{N}]/u.test(next)) {
      tokens.push({ start: i, end: i + needle.length });
      out = out.slice(0, i) + ' '.repeat(needle.length) + out.slice(i + needle.length);
    }
  }
  return tokens.length ? { body: out, tokens } : null;
}

/** Text nach einem „@“ vor der Schreibmarke (Anfang oder nach Leerraum), höchstens 40 Zeichen, ohne Zeilenwechsel. */
export function mentionAt(text: string, caret: number): { start: number; caret: number; query: string } | null {
  const before = text.slice(0, caret);
  const start = before.lastIndexOf('@');
  if (start < 0 || (start > 0 && !/\s/.test(before[start - 1]!))) return null;
  const query = before.slice(start + 1);
  if (query.length > 40 || /[\n@]/.test(query)) return null;
  return { start, caret, query };
}

/** Personen zur @-Auswahl: Name beginnt mit dem Text oder ein Namensteil beginnt damit. */
export function mentionMatches<T extends MentionPerson>(people: readonly T[], query: string): T[] {
  const q = query.toLocaleLowerCase('de-DE');
  return people.filter((p) => {
    const name = p.displayName.toLocaleLowerCase('de-DE');
    return name.startsWith(q) || name.split(/\s+/).some((part) => part.startsWith(q));
  });
}

